"use client";

import { useEffect, useRef } from "react";

type Particle = {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  radius: number;
  color: string;
  phase: number;
  pulseSpeed: number;
  opacity: number;
};

const COLORS = ["0, 220, 255", "255, 60, 180", "120, 80, 255"];

export function NeonPulseBackdrop() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    let width = 0;
    let height = 0;
    let particles: Particle[] = [];
    let animationFrame = 0;
    const startedAt = performance.now();

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * devicePixelRatio;
      canvas.height = height * devicePixelRatio;
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);

      const count = Math.max(10, Math.min(42, Math.round((width * height) / 12000)));
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        velocityX: (Math.random() - 0.5) * 0.24,
        velocityY: (Math.random() - 0.5) * 0.2,
        radius: Math.random() * 1.8 + 0.7,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        phase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.6 + 0.35,
        opacity: Math.random() * 0.28 + 0.14,
      }));
    };

    const draw = (now: number) => {
      context.clearRect(0, 0, width, height);
      const elapsed = (now - startedAt) / 1000;

      for (const particle of particles) {
        particle.x = (particle.x + particle.velocityX + width) % width;
        particle.y = (particle.y + particle.velocityY + height) % height;
        const pulse = Math.sin(elapsed * particle.pulseSpeed + particle.phase) * 0.5 + 0.5;
        const opacity = particle.opacity * (0.45 + pulse * 0.55);
        const radius = particle.radius * (0.8 + pulse * 0.35);
        const glow = context.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          radius * 5,
        );
        glow.addColorStop(0, `rgba(${particle.color}, ${opacity})`);
        glow.addColorStop(0.35, `rgba(${particle.color}, ${opacity * 0.25})`);
        glow.addColorStop(1, `rgba(${particle.color}, 0)`);

        context.beginPath();
        context.arc(particle.x, particle.y, radius * 5, 0, Math.PI * 2);
        context.fillStyle = glow;
        context.fill();
      }

      animationFrame = requestAnimationFrame(draw);
    };

    resize();
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(canvas);
    animationFrame = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, []);

  return <canvas ref={canvasRef} aria-hidden className="pointer-events-none absolute inset-0 z-0 size-full" />;
}